import React, {memo, useCallback, useEffect, useMemo, useState} from 'react';
import {PasswordContainer} from '../../../../../components/PasswordContainer';
import {SettingsPageContainer} from '../../../../../components/Settings/SettingsPageContainer';
import {useHistory, useParams} from 'react-router-dom';
import {useMessage} from '../../../../../hooks/message';
import {useKeyboard} from '../../../../../hooks/keyboard';
import {useRPC} from '../../../../../hooks/rpc';
import {SettingInputComponent} from '../../../../../components/Settings/SettingInputComponent';
import {isValidIP, isValidPort, isValidSIPNo} from '../../../../../utils/validation';
import {GatewayModel} from '../../../../../types/gateway';
import {useFilterSelector} from '../../../../../hooks/filter-selector';
import {SettingReadOnlyRowComponent} from '../../../../../components/Settings/SettingReadOnlyRowComponent';
import {useLocalization} from '../../../../../hooks/localization';
import {SettingCheckBoxComponent} from "../../../../../components/Settings/SettingCheckboxComponent";
import { SettingSelectComponent } from '../../../../../components/Settings/SettingSelectComponent';
import { useSelector } from 'react-redux';
import { SelectItem } from '../../../../../components/Select';

const BACK_URL = '/settings/gateway';

export const SIPDetailScreen = memo(function SIPDetailScreen() {
    const {t} = useLocalization();
    const params = useParams<{ id: string }>();
    const {success, failure} = useMessage();
    const formatList = useSelector(state => state.control.sipFormatTypeList)
    const [setActiveInput, renderKeyboard, currentInput] = useKeyboard();
    const [setActiveNumpadInput, renderNumpadKeyboard, currentNumpadInput] = useKeyboard('numpad', true);
    const [setActiveNumpadExtendedInput, renderNumpadExtendedKeyboard, currentNumpadExtendedInput] =
        useKeyboard('numpad-extended', true);
    const [rpc] = useRPC();
    const history = useHistory();

    const filteredGatewayList = useFilterSelector(
        state => state.control.gatewayList,
        gateway => gateway.id === params.id);
    const selectedGateway = filteredGatewayList.length === 0 ? null : filteredGatewayList[0];

    const [userId, setUserId] = useState('');
    const [password, setPassword] = useState('');
    const [ip, setIP] = useState('');
    const [port] = useState('5060');
    const [flatName, setFlatName] = useState('');
    const [flatNumber, setFlatNumber] = useState("")
    const [sipStatus, setSipStatus] = useState(false);
    const onBackPressed = useCallback(() => {
        history.push(BACK_URL);
    }, [history]);

    const [hasSip, setHasSip] = useState(true);


    const formatTypeList: SelectItem<string>[] = useMemo(() => {
        const count = formatList.length || 0
        if (count === 0) {
            return []
        }
        const list = []
        for (const values of formatList) {
            list.push({
                value: values?.id,
                label: values.name
            })
        }
        return list
    }, [formatList])

    const [format, setFormat] = useState<SelectItem<string>>(
        formatTypeList.find(format => format.value === selectedGateway.settings?.sip?.sip_format) || null
    )


    const isFieldsValid = useCallback((): boolean => {
        if (!isValidSIPNo(userId) && hasSip) {
            failure(t('pls_enter_a_valid_sip_user_no'));

            return false;
        }

        if (password == null || password.length === 0 && hasSip) {
            failure(t('pls_enter_a_valid_password'));

            return false;
        }

        if (!isValidIP(ip)) {
            failure(t('pls_enter_a_valid_ip'));

            return false;
        }

        if (!isValidPort(port) && hasSip) {
            failure(t('pls_enter_a_valid_port'));

            return false;
        }

        if (!hasSip) {
            if(format == null){
                failure(t("pls_enter_a_format"))
                return false
            }
        }

        if (flatName == null || flatName.length === 0 || flatName.length > 1 || flatName.match(/[0-9]/g) && hasSip) {
            failure(t("sip_page.pls_enter_a_valid_block_name"))

            return false
        }

        if (flatNumber == null || flatNumber.length === 0 || isNaN(Number(flatNumber)) && hasSip) {
            failure(t("sip_page.pls_enter_a_valid_apartment_no"))

            return false
        }

        return true;
    }, [
        t,
        failure,
        userId,
        password,
        ip,
        port,
        flatName,
        flatNumber,
        hasSip,
        format
    ]);

    const onSavePressed = useCallback(() => {
        if (!isFieldsValid()) {
            return
        }

        const gatewayModel: GatewayModel = {
            ...selectedGateway,
            settings: {
                ...selectedGateway.settings,
                sip: {
                    user_id: Number(userId),
                    password,
                    server_ip: ip,
                    server_port: Number(port),
                    flat_name: `${flatName.toUpperCase()}${flatNumber}`,
                    sip_status: sipStatus,
                    has_sip: hasSip,
                    sip_format: format?.value
                }
            }
        }

        if (!hasSip) {
            gatewayModel.settings.sip.password = ""
            gatewayModel.settings.sip.user_id = 0
        }

        rpc.updateGateway(gatewayModel)
            .then(() => {
                success(t("successfully_saved"))
                onBackPressed()
            })
            .catch(failure)
    }, [
        t,
        isFieldsValid,
        rpc,
        success,
        failure,
        onBackPressed,
        userId,
        password,
        ip,
        port,
        flatName,
        sipStatus,
        selectedGateway,
        flatNumber,
        hasSip,
        format?.value
    ])

    const onBlur = useCallback(() => {
        setActiveInput(null);
        setActiveNumpadInput(null);
        setActiveNumpadExtendedInput(null);
    }, [setActiveInput, setActiveNumpadInput, setActiveNumpadExtendedInput]);

    const buttons = useMemo(() => [
        {
            text: t('save'),
            className: 'green',
            onClick: onSavePressed,
        },
        {
            text: t('cancel'),
            className: 'red',
            url: BACK_URL,
        },
    ], [t, onSavePressed]);

    useEffect(() => {
        if (selectedGateway == null) {
            onBackPressed();

            return;
        }

        setUserId(selectedGateway.settings?.sip?.user_id?.toString() || '');
        setPassword(selectedGateway.settings?.sip?.password || '');
        setIP(selectedGateway.settings?.sip?.server_ip || '');
        // setPort(selectedGateway.settings?.sip?.server_port?.toString() || '');
        setFlatName(selectedGateway.settings?.sip?.flat_name[0] || "")
        setFlatNumber(selectedGateway.settings?.sip?.flat_name.slice(1) || "")
        setSipStatus(selectedGateway.settings?.sip?.sip_status || false);
        setHasSip(selectedGateway.settings?.sip?.has_sip || false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <PasswordContainer requiredRole="service" isFullscreen={true}>
            <SettingsPageContainer
                className="service-edit"
                buttons={buttons}
                backUrl={BACK_URL}
                currentInput={currentInput}
                currentNumpadInput={currentNumpadInput}
                currentNumpadExtendedInput={currentNumpadExtendedInput}
                title={selectedGateway?.name || ''}>
                <div className="content-divider">
                    <div className="content-wrapper">
                        <SettingCheckBoxComponent
                            label={t('sip_status')}
                            set={() => setSipStatus(r => !r)}
                            value={sipStatus}/>
                        <SettingCheckBoxComponent
                            label={t('Sip')}
                            set={() => setHasSip(r => !r)}
                            value={hasSip}/>
                    {
                        renderIf(hasSip)(() =>
                        <SettingInputComponent
                        placeholder={t('write_sip_user_no')}
                        label={t('sip_user_no')}
                        value={userId}
                        setValue={setUserId}
                        setActiveInput={setActiveNumpadInput}
                        onBlur={onBlur}/>
                        )
                    }
                    {
                        renderIf(hasSip)(() =>
                        <SettingInputComponent
                            type="password"
                            placeholder={t('write_password')}
                            label={t('password')}
                            value={password}
                            setValue={setPassword}
                            setActiveInput={setActiveInput}
                            onBlur={onBlur}/>
                        )
                    }
                        <SettingInputComponent
                            placeholder={t('write_sip_ip')}
                            label={t(hasSip===true? 'sip_ip': 'ip')}
                            value={ip}
                            setValue={setIP}
                            setActiveInput={setActiveNumpadExtendedInput}
                            onBlur={onBlur}/>
                       {renderIf(!hasSip)(() => (
                       <SettingSelectComponent
                            label={t('format')}
                            options={formatTypeList}
                            onChange={setFormat}
                            value={format}/>
                       )) }
                    {
                        renderIf(hasSip)(() =>
                        <SettingReadOnlyRowComponent
                            title="PORT"
                            value={port}/>
                        )
                    }
                        {/*<SettingInputComponent*/}
                        {/*    placeholder='Port yazınız...'*/}
                        {/*    label='Port'*/}
                        {/*    value={port}*/}
                        {/*    setValue={setPort}*/}
                        {/*    setActiveInput={setActiveNumpadInput}*/}
                        {/*    onBlur={onBlur}/>*/}
                    </div>
                    <div className="content-wrapper">
                        <SettingInputComponent
                            placeholder={t('sip_page.enter_block_name')}
                            label={t('sip_page.block')}
                            value={flatName}
                            setValue={setFlatName}
                            setActiveInput={setActiveInput}
                            onBlur={onBlur}/>
                        <SettingInputComponent
                            placeholder={t('sip_page.enter_apartment_no')}
                            label={t('sip_page.apartment_no')}
                            value={flatNumber}
                            setValue={setFlatNumber}
                            setActiveInput={setActiveInput}
                            onBlur={onBlur}/>
                    </div>
                </div>
            </SettingsPageContainer>
            {
                renderKeyboard()
            }
            {
                renderNumpadKeyboard()
            }
            {
                renderNumpadExtendedKeyboard()
            }
        </PasswordContainer>
    );
});
